import Add from "@mui/icons-material/Add";
import LinkOff from "@mui/icons-material/LinkOff";
import {
  Box,
  Button,
  IconButton,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { manifestNumberLabel } from "encamp-shared/src/waste/shippingDocumentLabels";
import { Link } from "react-router-dom";
import { CompleteStaffCorrectionButton } from "routes/Customer/Waste/Manifests/CompleteStaffCorrectionButton";

import {
  correctedToValue,
  correctionFieldLabel,
  correctionLineLabel,
  correctionPathLabel,
  correctionStateLabel,
  correctionValueLabel,
  isCorrectionClosedUnattributed,
  isCorrectionLanded,
  ManifestCorrectionRow,
} from "./manifestCorrectionDisplay";

type Props = {
  corrections: ManifestCorrectionRow[];
  /** Builds the per-row manifest link-outs. */
  tenantId: string;
  title?: string;
  /** Shows the table even when empty, so the first correction can be linked. */
  onAdd?: () => void;
  onRemove?: (correction: ManifestCorrectionRow) => void;
  disabled?: boolean;
};

/** Correction dates use UTC to match the Manifest Review page. */
export function ManifestCorrectionsTable({
  corrections,
  tenantId,
  title = "Manifest corrections",
  onAdd,
  onRemove,
  disabled = false,
}: Props) {
  if (corrections.length === 0 && !onAdd) return null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" component="h4">
          {title}
        </Typography>
        {onAdd && (
          <Button size="small" startIcon={<Add />} onClick={onAdd} disabled={disabled}>
            Add
          </Button>
        )}
      </Stack>
      {corrections.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No manifest corrections are linked.
        </Typography>
      ) : (
        <TableContainer
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            // Eight columns overflow the drawer at its narrower width at default padding.
            "& .MuiTableCell-root": { px: 1.5 },
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Manifest</TableCell>
                <TableCell>Line</TableCell>
                <TableCell>Field</TableCell>
                <TableCell>Corrected to</TableCell>
                <TableCell>State</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Corrected in</TableCell>
                <TableCell>Corrected</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {corrections.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <MuiLink
                      component={Link}
                      to={`/o/${tenantId}/waste/manifests/${c.processedManifestId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {manifestNumberLabel(c)}
                    </MuiLink>
                    {c.shippedDate && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        shipped {getUtcDisplayDate(c.shippedDate, "med")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{correctionLineLabel(c)}</TableCell>
                  <TableCell>{correctionFieldLabel(c.field)}</TableCell>
                  <TableCell>{correctionValueLabel(c.field, correctedToValue(c))}</TableCell>
                  <TableCell>
                    <StatusChip color={correctionChipColor(c)} label={correctionStateLabel(c)} />
                  </TableCell>
                  <TableCell>{correctionPathLabel(c.resolutionPath)}</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {c.resolvedAt ? getUtcDisplayDate(c.resolvedAt, "med") : "—"}
                    {c.resolvedByName ? ` · ${c.resolvedByName}` : ""}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <CompleteStaffCorrectionButton correction={c} />
                      {onRemove && (
                        <Tooltip title="Remove from this review">
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Remove ${manifestNumberLabel(c)} ${correctionLineLabel(c)} ${correctionFieldLabel(c.field)}`}
                              onClick={() => onRemove(c)}
                              disabled={disabled}
                            >
                              <LinkOff fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

/** Landed reads as done, an unattributed close as a problem, in flight as neutral. */
function correctionChipColor(row: ManifestCorrectionRow): "success" | "warning" | "info" {
  if (isCorrectionLanded(row)) return "success";
  if (isCorrectionClosedUnattributed(row)) return "warning";
  return "info";
}
